import { Property } from "../types/property-types";
import { getDistanceMatrix } from "../data/travel-time/travel-time-fetcher";
import {
  TravelTimeDistanceMatrixArgs,
  TravelTimeDistanceMatrixResponseResult,
} from "../types/travel-time-types";
import { placesNearbySearch } from "../data/google/places-nearby-search";
import { NearbySearchArgs, Place } from "../types/google-types";

/**
 * Retrieves nearby places for a given property using the provided search arguments.
 *
 * @param property - The property for which to find nearby places.
 * @param nearbySearchArgs - The arguments to use for the nearby search.
 * @returns A promise that resolves to an array of nearby places.
 *
 * @remarks
 * This function first checks if the property's location has valid latitude and longitude.
 * If not, it returns an empty array. Otherwise, it performs a nearby search using the
 * provided latitude, longitude, and search arguments. If places are found, it calculates
 * the travel time and distance matrix for the places and sets the commute data.
 *
 * @example
 * ```typescript
 * const property: Property = { location: { latitude: 40.7128, longitude: -74.0060 } };
 * const searchArgs: NearbySearchArgs = { radius: 1000, type: 'restaurant' };
 *
 * getPropertyNearbyPlaces(property, searchArgs).then((places) => {
 *
 * });
 * ```
 */
export const getPropertyNearbyPlaces = async (
  property: Property,
  nearbySearchArgs: NearbySearchArgs
): Promise<Place[]> => {
  if (
    !process.env.PdpNearbyPlacesEnabled ||
    process.env.PdpNearbyPlacesEnabled !== "true"
  ) {
    return [];
  }

  if (!property.location?.latitude || !property.location?.longitude) {
    return [];
  }

  const places = await placesNearbySearch(
    property.location.latitude,
    property.location.longitude,
    nearbySearchArgs
  ).then(async (places) => {
    if (places?.places) {
      // Now calculate commute distance to nearby places using the TravelTime API
      const date = new Date();

      const distanceMatrixArgs: TravelTimeDistanceMatrixArgs = {
        origin: property,
        destinations: places.places,
        departureTime: new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          9,
          0,
          0
        ),
        drivingTime: 3600,
        walkingTime: 720,
      };

      await getDistanceMatrix(distanceMatrixArgs).then((results) => {
        setPlacesCommuteData(results, places.places);
      });

      return places.places;
    } else {
      return [];
    }
  });

  return places;
};

/**
 * Updates the travel time and distance information for a list of places based on the provided travel time distance matrix results.
 *
 * @param {TravelTimeDistanceMatrixResponseResult[] | null} results - The travel time distance matrix results containing travel time and distance information for various locations.
 * @param {Place[]} places - The list of places to be updated with travel time and distance information.
 */
const setPlacesCommuteData = (
  results: TravelTimeDistanceMatrixResponseResult[] | null,
  places: Place[]
) => {
  if (results?.length) {
    results.forEach((result) => {
      result.locations.forEach((location) => {
        const nearbyPlace = places.find((place) => place.id === location.id);
        if (nearbyPlace) {
          nearbyPlace.travelTime = {
            distanceToOrigin: location.properties[0].distance,
            timeToOrigin: location.properties[0].travel_time,
          };
        }
      });
    });
  }
};
